/**
 * Soft≠Live Fleet helpers — normalize Live OTA + ops metrics only.
 * Never invent campaign rows, queue depths, or brochure KPIs.
 */

/** @param {unknown} envelope GET /admin/ops/global-metrics */
export function fleetHealthBlocksOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const kpi = data.kpi && typeof data.kpi === "object" ? data.kpi : {};
  const queues = data.queues && typeof data.queues === "object" ? data.queues : {};
  const flags = data.flags && typeof data.flags === "object" ? data.flags : {};
  const leader = data.leader && typeof data.leader === "object" ? data.leader : {};

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const pick = (...cands) => {
    for (const c of cands) {
      const n = num(c);
      if (n != null) return n;
    }
    return null;
  };
  const fmt = (n, empty = "—") => (n == null ? empty : String(n));

  const mqttOn = flags.MQTT_CONNECTED === true
    || flags.mqtt_connected === true
    || leader.mqtt === "connected"
    || data.mqtt_connected === true;
  const mqttKnown = flags.MQTT_CONNECTED != null
    || flags.mqtt_connected != null
    || leader.mqtt != null
    || data.mqtt_connected != null;

  const ingest = pick(queues.ingest, queues.ingest_depth, kpi.ingest_depth);
  const stream = pick(queues.stream, queues.telemetry, kpi.telemetry_waiting);
  const followup = pick(queues.followup, queues.notify_followup, kpi.followup_depth);
  const fcm = pick(queues.fcm, queues.push, kpi.fcm_depth);
  const batch = pick(queues.pg_batch, queues.batch, kpi.pg_batch_rows);
  const pool = pick(queues.pg_pool, kpi.pg_pool_checked_out, data.pg_pool_checked_out);
  const poolMax = pick(queues.pg_pool_max, kpi.pg_pool_max, data.pg_pool_max);
  const lease = pick(leader.lease_remaining_s, leader.ttl_s, kpi.leader_lease_s);

  const blocks = [
    {
      id: "mqtt",
      t: "Broker",
      tone: !mqttKnown ? "off" : (mqttOn ? "go" : "bad"),
      sub: mqttKnown ? (mqttOn ? "connected" : "not connected") : "not reported on this bus",
      v: mqttKnown ? (mqttOn ? "connected" : "down") : "—",
    },
    {
      id: "ingest",
      t: "Ingest queue",
      tone: ingest == null ? "off" : (ingest > 800 ? "warn" : "go"),
      sub: ingest == null ? "depth not on bus" : "waiting frames",
      v: fmt(ingest),
    },
    {
      id: "stream",
      t: "Telemetry stream",
      tone: stream == null ? "off" : "go",
      sub: stream == null ? "depth not on bus" : "waiting",
      v: fmt(stream),
    },
    {
      id: "followup",
      t: "Notify follow-up",
      tone: followup == null ? "off" : "go",
      sub: followup == null ? "depth not on bus" : "queue depth",
      v: fmt(followup),
    },
    {
      id: "fcm",
      t: "Push queue",
      tone: fcm == null ? "off" : "go",
      sub: fcm == null ? "depth not on bus" : "queue depth",
      v: fmt(fcm),
    },
    {
      id: "batch",
      t: "PG micro-batch",
      tone: batch == null ? "off" : (batch > 100 ? "warn" : "go"),
      sub: batch == null ? "rows not on bus" : "rows waiting",
      v: fmt(batch),
    },
    {
      id: "pool",
      t: "PG pool",
      tone: pool == null ? "off" : "go",
      sub: poolMax != null ? ("of " + poolMax) : (pool == null ? "not on bus" : "checked out"),
      v: pool == null ? "—" : (poolMax != null ? (pool + " / " + poolMax) : String(pool)),
    },
    {
      id: "leader",
      t: "Failover holder",
      tone: lease == null && !leader.holder ? "off" : "go",
      sub: leader.holder
        ? String(leader.holder)
        : (lease == null ? "lease not on bus" : "lease remaining"),
      v: lease == null ? (leader.holder ? "held" : "—") : (lease + " s"),
    },
  ];
  return blocks;
}

/** @param {unknown} envelope */
export function campaignRowsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.campaigns || (Array.isArray(root) ? root : []);
  if (!Array.isArray(items)) return [];
  return items.map((raw) => {
    const r = raw || {};
    const counters = r.counters && typeof r.counters === "object" ? r.counters : {};
    const runs = {
      pending: Number(counters.pending) || 0,
      dispatched: Number(counters.dispatched) || 0,
      success: Number(counters.success) || 0,
      failed: Number(counters.failed) || 0,
      rolled_back: Number(counters.rolled_back) || 0,
    };
    const decisions = Array.isArray(r.decisions) ? r.decisions : [];
    return {
      id: String(r.id || r.campaign_id || "").trim(),
      fw: String(r.fw_version || r.fw || "").trim() || "—",
      state: String(r.state || "dispatched").trim() || "dispatched",
      by: String(r.created_by || r.by || "").trim() || "—",
      at: String(r.created_at || r.updated_at || r.at || "").trim() || "—",
      notes: String(r.notes || "").trim(),
      admins: decisions.map((d) => ({
        u: String(d.admin_username || d.u || "").trim() || "—",
        d: String(d.action || d.d || "—").trim() || "—",
        at: String(d.decided_at || d.at || "").trim() || null,
      })),
      runs,
      raw: r,
    };
  }).filter((c) => c.id);
}

/** @param {unknown} envelope */
export function firmwareRowsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.firmwares || (Array.isArray(root) ? root : []);
  if (!Array.isArray(items)) return [];
  return items.map((raw) => {
    const r = raw || {};
    const size = Number(r.size);
    const sku = Array.isArray(r.hw_required_codes) ? r.hw_required_codes.map(String) : [];
    const sha = String(r.sha256 || "").trim();
    return {
      name: String(r.name || "").trim(),
      fw: String(r.fw_version || r.fw || "").trim() || "—",
      size: Number.isFinite(size) ? (size >= 1048576
        ? ((size / 1048576).toFixed(2) + " MB")
        : (Math.round(size / 1024) + " KB")) : "—",
      sha: sha ? (sha.slice(0, 4) + "…" + sha.slice(-4)) : "—",
      sku,
      notes: !!(r.hw_scope_file || r.notes),
    };
  }).filter((f) => f.name);
}

/** Offline devices from Live GET /devices — never invent a diagnose subject. */
export function quietDevicesOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.devices || (Array.isArray(root) ? root : []);
  if (!Array.isArray(items)) return [];
  return items
    .filter((d) => d && !(d.is_online === true || d.online === true || d.presence === "online"
      || String(d.status || "").toLowerCase() === "online"))
    .map((d) => {
      const id = String(d.id || d.device_id || "").trim();
      const age = Number(d.last_seen_age_s ?? d.age_s ?? d.offline_age_s);
      return {
        id,
        label: String(d.label || d.name || d.device_id || id || "").trim() || "—",
        zone: String(d.zone || d.group || "").trim(),
        ageS: Number.isFinite(age) && age >= 0 ? age : null,
        raw: d,
      };
    })
    .filter((d) => d.id);
}

/** Live suspect code → short bucket id (B0…B8). */
export function presenceBucketId(suspect) {
  const m = String(suspect || "").trim().match(/^B(\d)/i);
  return m ? ("B" + m[1]) : "";
}

/** B6 cache lag and B8 HTTP board-live are informational — not a Hardware Suspect banner. */
export function presenceSuspectIsProblem(suspect) {
  const s = String(suspect || "").trim();
  if (!s) return false;
  if (s === "B6_cache_or_ui_lag" || s === "B8_http_board_live") return false;
  return /^B[0-7]_/.test(s);
}

/**
 * Normalize GET /devices/{id}/presence-diagnose for the Fleet desk.
 * @param {unknown} diag
 */
export function presenceDiagnoseView(diag) {
  if (!diag || typeof diag !== "object") return null;
  const evidence = diag.evidence && typeof diag.evidence === "object" ? diag.evidence : {};
  const decision = diag.decision && typeof diag.decision === "object" ? diag.decision : {};
  const net = evidence.net_health && typeof evidence.net_health === "object" ? evidence.net_health : {};
  const threshold = Number(decision.offline_threshold_s ?? evidence.offline_threshold_s);
  const ages = {
    heartbeat: numAge(evidence.last_heartbeat_age_s),
    signal: numAge(evidence.last_signal_age_s),
    updated: numAge(evidence.last_updated_age_s ?? decision.row_age_s),
    status: numAge(evidence.last_status_age_s),
    board: numAge(evidence.board_report_age_s),
  };
  const suspect = String(diag.suspect || "").trim();
  return {
    deviceId: String(diag.device_id || "").trim(),
    online: !!diag.is_online,
    suspect,
    problem: presenceSuspectIsProblem(suspect),
    bucket: presenceBucketId(diag.suspect),
    nextAction: String(diag.next_action || "").trim(),
    reason: String(decision.effective_online_reason || "").trim(),
    thresholdS: Number.isFinite(threshold) && threshold > 0 ? threshold : null,
    ages,
    net: {
      wifi: numOrNull(net.wifi_reconnects),
      mqtt: numOrNull(net.mqtt_reconnects),
      failStreak: numOrNull(net.mqtt_fail_streak),
      longestGapMs: numOrNull(net.mqtt_longest_gap_ms),
    },
    raw: diag,
  };
}

function numAge(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
